import { capitalize } from "lodash"
import { useMemo } from "react"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "../context/dashboard-id"
import { DEFAULT_TRADE_STATE } from "../store/trade-slice"
import { INSTRUMENTS } from "."
import { getIsNoLongerHeld } from "./positions"

const COMPOSITION_SUFFIX = "composition"
const MARKET_VALUE_SUFFIX = "marketValue"
const ADDITIONAL_INSTRUMENTS = ["comparison", "rebalanced"]

function extractOriginalData(original, originalMarketValue) {
  return {
    positionPercentEquity: original?.positionSetEconomicExposure,
    economicExposure: originalMarketValue?.positionSetEconomicExposure,
    basePercentEquity: original?.baseEconomicExposure,
  }
}

function buildTooltipClassification(position) {
  if (!position) return null

  if (position.sector || position.industry) {
    return {
      name: position.sector || position.industry,
    }
  }
  return null
}

function buildTooltipData(position, originalComposition, originalMarketValue, originalPerformance, additionalInstruments, trades) {
  const originalData = extractOriginalData(originalComposition, originalMarketValue)

  const additionalItems = additionalInstruments.map(instrument => ({
    label: capitalize(instrument.type),
    percentEquity: instrument.composition?.positionSetEconomicExposure,
    economicExposure: instrument.marketValue?.positionSetEconomicExposure,
  }))

  const isActive = !!originalData.basePercentEquity
  const isUnmodeled = position?.isUnmodeled
  const isNotHeld = position.notHeld
  const isNoLongerHeld = getIsNoLongerHeld(
    position,
    originalComposition,
    originalPerformance,
    trades,
  )

  const originalPerformanceContributor = originalPerformance?.descriptors
  const positionComposition = originalComposition || additionalInstruments.find(inst => inst.composition)?.composition || originalPerformanceContributor
  const classification = buildTooltipClassification(originalPerformanceContributor)
  const ticker = originalComposition ? positionComposition.assetClass && positionComposition.identifier : originalPerformanceContributor?.ticker

  return {
    ...positionComposition,
    id: position.id,
    ...originalData,
    additionalItems,
    isNotHeld,
    isNoLongerHeld,
    isUnmodeled,
    isActive,
    classification,
    ticker,
    description: positionComposition?.description || positionComposition?.identifier,
  }
}
function hasAnyCompositionData(originalComposition, additionalInstruments) {
  return !!(originalComposition || additionalInstruments.some(inst => inst.composition))
}

function hasAnyPerformanceData(originalPerformance, additionalInstruments) {
  return !!(originalPerformance || additionalInstruments.some(inst => inst.performance))
}

function getInstrumentData(suffix, values) {
  return {
    composition: values[`${COMPOSITION_SUFFIX}${suffix}`],
    marketValue: values[`${MARKET_VALUE_SUFFIX}${suffix}`],
  }
}

function hasInstrumentData(instrumentData) {
  return !!(instrumentData.composition && instrumentData.marketValue)
}

function getAdditionalInstrumentsWithData(values, instruments) {
  return instruments.map(instrument => {
    const data = getInstrumentData(instrument.metricsSuffix, values)
    return hasInstrumentData(data) ? instrument : null
  })
}

function parseInstrumentData(values, instrument) {
  if (!instrument) {
    return { composition: null, marketValue: null }
  }

  return getInstrumentData(instrument.metricsSuffix, values)
}

const ATTRIBUTION_SUMMARY_IDS = ["Specific", "Factors", "Trading", "Residual"]

function isFactorOrHasFactorChildren(position) {
  if (position.isFactor) {
    return true
  }

  // Attribution summary rows (Specific, Factors, Trading, Residual) should not show position tooltips
  if (ATTRIBUTION_SUMMARY_IDS.includes(position.id)) {
    return true
  }

  if (position.children && Object.values(position.children).some(child => child.isFactor)) {
    return true
  }

  return false
}

export default function usePositionTooltip(position) {
  const dashboardId = useDashboardId()
  const trades = useAppSelector((s) => s.dashboard.trade[dashboardId]?.trades ?? DEFAULT_TRADE_STATE.trades)

  return useMemo(() => {
    if (position.tooltipValue) {
      return position.tooltipValue
    }

    // We don't want to show position tooltips for factor-related labels
    if (isFactorOrHasFactorChildren(position)) {
      return null
    }

    if (!position?.values) {
      return null
    }

    const values = position.values

    const additionalInstrumentsConfig = ADDITIONAL_INSTRUMENTS.map(instrument => ({
      metricsSuffix: INSTRUMENTS[instrument].metricsSuffix,
      type: instrument,
    }))

    const originalComposition = values.composition
    const originalMarketValue = values.marketValue
    const originalPerformance = values.performance

    const additionalInstrumentsWithData = getAdditionalInstrumentsWithData(values, additionalInstrumentsConfig)

    const additionalInstruments = additionalInstrumentsWithData
      .filter(instrument => instrument !== null)
      .map(instrument => {
        const { composition, marketValue } = parseInstrumentData(values, instrument)
        return {
          type: instrument.type,
          composition,
          marketValue,
        }
      })

    if (!hasAnyCompositionData(originalComposition, additionalInstruments) && !hasAnyPerformanceData(originalPerformance, additionalInstruments)) {
      return null
    }

    return buildTooltipData(position, originalComposition, originalMarketValue, originalPerformance, additionalInstruments, trades)
  }, [position, trades])
}
